import { useEffect, useRef, useState, useCallback } from 'react';
import { websocketManager, WebSocketMessage } from '../services/websocketService';

export interface UseWebSocketOptions {
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  onReconnect?: () => void;
}

export function useWebSocket() {
  const [socketState, setSocketState] = useState<'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED' | 'UNKNOWN'>('CLOSED');
  const [lastSocketEvent, setLastSocketEvent] = useState<string>('');
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const wsThreadRef = useRef<string | null>(null);
  const wsConnectPromiseRef = useRef<Promise<void> | null>(null);
  const wsConnectingThreadRef = useRef<string | null>(null);

  const connectToThread = useCallback(async (threadId: string, options: UseWebSocketOptions = {}): Promise<void> => {
    if (!threadId) return;
    
    console.log(`🔌 Connecting to WebSocket for thread: ${threadId}`);
    
    // If already connected to this thread, do nothing
    if (socketState === 'OPEN' && wsThreadRef.current === threadId) {
      console.log(`✅ Already connected to thread ${threadId}`);
      return;
    }
    
    // If a connect is already in-flight for this thread, reuse it
    if (wsConnectPromiseRef.current && wsConnectingThreadRef.current === threadId) {
      console.log(`🔄 Connection already in-flight for thread ${threadId}`);
      return wsConnectPromiseRef.current;
    }

    const token = localStorage.getItem('token') || '';
    
    // Disconnect from previous thread if needed
    if (wsThreadRef.current && wsThreadRef.current !== threadId) {
      try { 
        websocketManager.disconnect(wsThreadRef.current); 
      } catch {}
    }
    
    wsThreadRef.current = threadId;
    wsConnectingThreadRef.current = threadId;

    wsConnectPromiseRef.current = new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        wsConnectPromiseRef.current = null;
        wsConnectingThreadRef.current = null;
        reject(new Error('WebSocket connection timeout'));
      }, 5000);

      websocketManager.connect(threadId, token, {
        onMessage: (message) => {
          console.log('🔥🔥🔥 WEBSOCKET RAW MESSAGE RECEIVED:', message.type, message);
          setLastSocketEvent(message.type);
          options.onMessage?.(message);
        },
        onConnect: () => {
          clearTimeout(timeout);
          setSocketState('OPEN');
          setIsReconnecting(false);
          setReconnectAttempt(0);
          console.log('🔥🔥🔥 WEBSOCKET CONNECTED TO THREAD:', threadId);
          wsConnectPromiseRef.current = null;
          wsConnectingThreadRef.current = null;
          
          // Clear reconnection timeout on successful connection
          if (reconnectTimeoutRef.current) {
            window.clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          
          options.onConnect?.();
          resolve();
        },
        onReconnect: () => {
          clearTimeout(timeout);
          setSocketState('OPEN');
          setIsReconnecting(false);
          setReconnectAttempt(0);
          console.log('🔄 WebSocket reconnected:', threadId);
          wsConnectPromiseRef.current = null;
          wsConnectingThreadRef.current = null;
          
          // Clear reconnection timeout on successful reconnection
          if (reconnectTimeoutRef.current) {
            window.clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          
          options.onReconnect?.();
          resolve();
        },
        onDisconnect: () => {
          clearTimeout(timeout);
          setSocketState('CLOSED');
          setIsReconnecting(true); // Start showing reconnection indicator
          console.log('🔌 WebSocket disconnected:', threadId);
          wsConnectPromiseRef.current = null;
          wsConnectingThreadRef.current = null;
          
          // Set a timeout to stop showing "Reconnecting" if reconnection fails for too long
          if (reconnectTimeoutRef.current) {
            window.clearTimeout(reconnectTimeoutRef.current);
          }
          reconnectTimeoutRef.current = window.setTimeout(() => {
            console.log('⏰ Reconnection timeout reached, stopping reconnection indicator');
            setIsReconnecting(false);
            setReconnectAttempt(0);
          }, 15000); // 15 seconds
          
          options.onDisconnect?.();
        },
        onError: () => {
          clearTimeout(timeout);
          setSocketState('UNKNOWN');
          setIsReconnecting(true); // Start showing reconnection indicator on error
          console.warn('❌ WebSocket error:', threadId);
          wsConnectPromiseRef.current = null;
          wsConnectingThreadRef.current = null;
          
          // Set a timeout to stop showing "Reconnecting" if reconnection fails for too long
          if (reconnectTimeoutRef.current) {
            window.clearTimeout(reconnectTimeoutRef.current);
          }
          reconnectTimeoutRef.current = window.setTimeout(() => {
            console.log('⏰ Reconnection timeout reached after error, stopping reconnection indicator');
            setIsReconnecting(false);
            setReconnectAttempt(0);
          }, 120000); // 2 minutes
          
          options.onError;
          reject(new Error('WebSocket connection failed'));
        },
        onReconnectAttempt: (attempt) => {
          setReconnectAttempt(attempt);
          setIsReconnecting(true);
          console.log(`🔄 Reconnection attempt ${attempt} for thread: ${threadId}`);
        },
      });
    });

    return wsConnectPromiseRef.current;
  }, [socketState]);

  const disconnect = useCallback(() => {
    if (wsThreadRef.current) {
      websocketManager.disconnect(wsThreadRef.current);
      wsThreadRef.current = null;
    }
    
    // Clear reconnection timeout on manual disconnect
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    setSocketState('CLOSED');
    setIsReconnecting(false);
    setReconnectAttempt(0);
  }, []);

  const disconnectAll = useCallback(() => {
    websocketManager.disconnectAll();
    wsThreadRef.current = null;
    
    // Clear reconnection timeout on disconnect all
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    setSocketState('CLOSED');
    setIsReconnecting(false);
    setReconnectAttempt(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear reconnection timeout on unmount
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
      }
      disconnectAll();
    };
  }, [disconnectAll]);

  return {
    socketState,
    lastSocketEvent,
    isReconnecting,
    reconnectAttempt,
    connectToThread,
    disconnect,
    disconnectAll,
    connectedThreadId: wsThreadRef.current
  };
}